import React, { useMemo, useCallback, useEffect } from "react";
import FormInput from "Common/Custom/FormInput";
import FormSubmitButton from "Common/Custom/FormSubmitButton";
import PageBody from "Common/Custom/PageBody";
import { HeadingOne } from "Common/Custom/HeadingOne";
import FormSelect from "Common/Custom/FormSelect";
import FormDatePicker from "Common/Custom/ReactFlatpickr";
import FormTextarea from "Common/Custom/FormTextarea";
import FormSlotRadio from "../../SlotRadioButton/FormSlotRadio";
import propTypes from "prop-types";
import { getSelectOptions } from "utils/optionHelper";
import BillSummary from "./BillSummary";

const EditParkingBookingForm = ({
  values = {},
  errors = {},
  handleChange = () => {},
  handleSubmit = () => {},
  loading = false,
  title = "Update Booking",
  fields = [],
  BookingNumberOptions = [],
  VehicleOptions = [],
  ZoneTypeOptions = [],
  SlotTimeOptions = [],
  SlotTypeOptions = [],
  SettingOptions = {},
  FIELD_DEPENDENCIES = {}
}) => {
  // Configuration for select options
  const optionConfig = useMemo(() => ({
    booking_number: {
      list: BookingNumberOptions,
      valueKey: "booking_number",
      labelKey: "booking_number"
    },
    vehicle_list_id: {
      list: VehicleOptions,
      valueKey: "vehicle_list_id",
      labelKey: "vehicle_type"
    },
    zone_list_id: {
      list: ZoneTypeOptions,
      valueKey: "zone_list_id",
      labelKey: "zone_type"
    },
    setting_id: {
      list: SlotTimeOptions,
      valueKey: "setting_id",
      labelKey: "hours"
    },
    slot_list_id: {
      list: SlotTypeOptions,
      valueKey: "slot_list_id",
      labelKey: "slot_type"
    }
  }),
    [
      BookingNumberOptions,
      VehicleOptions,
      ZoneTypeOptions,
      SlotTimeOptions,
      SlotTypeOptions
    ]
  );

  // Calculate bill summary data
  const tableOption = useMemo(() => {
    const zone = ZoneTypeOptions?.find(
      o => o.zone_list_id === values.zone_list_id
    );

    const slotTime = SlotTimeOptions?.find(
      o => o.setting_id === values.setting_id
    );

    // Get selected slot count
    const selectedSlotIds = Array.isArray(values.slot_list_id)
      ? values.slot_list_id
      : (values.slot_list_id ? [values.slot_list_id] : []);

    const selectedSlotCount = selectedSlotIds.length;

    // Get slot names for display
    const selectedSlots = SlotTypeOptions?.filter(
      o => selectedSlotIds.includes(Number(o.slot_list_id))
    ) || [];

    const slotNames = selectedSlots.map(s => s.slot_type).join(', ') || "No Data";

    // Calculate pricing
    const pricePerSlot = Number(SettingOptions?.price || 0);
    const tax = Number(SettingOptions?.tax || 0);

    // Total price = price per slot × number of slots
    const totalPrice = pricePerSlot * selectedSlotCount;
    const taxAmount = (totalPrice * tax) / 100;
    const total = totalPrice + taxAmount;

    return {
      zone_type: zone?.zone_type || "No Data",
      hours: slotTime?.hours || "No Data",
      slot: slotNames,
      slotCount: selectedSlotCount,
      pricePerSlot: pricePerSlot,
      price: totalPrice,
      tax,
      taxAmount,
      total
    };
  },
    [
      values.zone_list_id,
      values.setting_id,
      values.slot_list_id,
      ZoneTypeOptions,
      SlotTimeOptions,
      SlotTypeOptions,
      SettingOptions
    ]
  );

  // Check if field should be disabled based on dependencies
  const isFieldDisabled = useCallback((fieldName) => {
    const dependencies = FIELD_DEPENDENCIES[fieldName];
    if (!dependencies) return false;
    return dependencies.some(dep => !values[dep]);
  }, [
    values
  ]);

  // Transform slot data for radio component
  const getSlotRadioOptions = useCallback(
    (slotList = []) => {
      if (!Array.isArray(slotList)) return [];

      const selectedSlotIds = values?.Old_slot_id || [];

      return slotList.map(slot => {
        const isBooked = slot.slot_booking_status === "booked";
        const isSelected = selectedSlotIds.includes(slot.slot_list_id);

        return {
          value: String(slot.slot_list_id),
          label: slot.slot_type,
          status: slot.slot_booking_status,

          // ✅ CORE LOGIC
          disabled: isBooked && !isSelected,

          icon: (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="svg-stroke"
              width="24"
              viewBox="0 0 24 24"
              stroke="currentColor"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M7 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
              <path d="M17 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
              <path d="M5 17h-2v-6l2 -5h9l4 5h1a2 2 0 0 1 2 2v4h-2m-4 0h-6m-6 -6h15m-6 0v-5" />
            </svg>
          ),

          Book_in: slot.check_In,
          Book_out: slot.check_out
        };
      });
    },
    [values?.slot_list_id, values?.Old_slot_id] // ✅ REQUIRED dependency
  );


  // Configure date picker constraints
  const getDatePickerConfig = useCallback((fieldName) => {
    if (fieldName !== "checkout_date") return {};

    return {
      minDate: values.checkin_date ? new Date(values.checkin_date) : new Date(),
      disabled: !values.checkin_date
    };
  }, [
    values.checkin_date
  ]);

  // Render individual form field
  const renderField = useCallback((field) => {
    switch (field.type) {
      case "select":
        return (
          <FormSelect
            key={field.name}
            field={field}
            value={values[field.name]}
            handleChange={handleChange}
            options={getSelectOptions(field.name, optionConfig)}
            error={errors[field.name]}
            disabled={isFieldDisabled(field.name)}
          />
        );

      case "date":
        const dateConfig = getDatePickerConfig(field.name);
        return (
          <FormDatePicker
            key={field.name}
            field={{ ...field, ...dateConfig }}
            value={values[field.name]}
            handleChange={handleChange}
            error={errors[field.name]}
            mode="datetime"
          />
        );

      case "textarea":
        return (
          <FormTextarea
            key={field.name}
            field={field}
            value={values[field.name]}
            handleChange={handleChange}
            error={errors[field.name]}
          />
        );

      case "slot_radio":
        if (!values.checkin_date || !values.checkout_date || !values.zone_list_id) return null;
        return (
          <FormSlotRadio
            key={field.name}
            field={field}
            options={getSlotRadioOptions(SlotTypeOptions)}
            value={values[field.name]}
            handleChange={handleChange}
            error={errors[field.name]}
            multiSelect={true} // Enable multi-select
          />
        );

      default:
        return (
          <FormInput
            key={field.name}
            field={field}
            value={values[field.name]}
            handleChange={handleChange}
            error={errors[field.name]}
          />
        );
    }
  }, [
    values,
    errors,
    handleChange,
    optionConfig,
    isFieldDisabled,
    getDatePickerConfig,
    getSlotRadioOptions,
    SlotTypeOptions
  ]);

  // Sync computed total_amount back to form state whenever it changes
  useEffect(() => {
    if (tableOption.total > 0) {
      handleChange({ target: { name: "total_amount", value: tableOption.total } });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableOption.total]);

  // Check if bill summary should be displayed
  const shouldShowBillSummary = useMemo(() => {
    return Boolean(
      values.zone_list_id &&
      values.setting_id &&
      values.vehicle_list_id &&
      values.booking_number &&
      values.checkin_date &&
      values.checkout_date
    );
  }, [
    values
  ]);
  
  return (
    <PageBody
      headerchildren={<HeadingOne>{title}</HeadingOne>}
      onSubmit={handleSubmit}
    >
      {fields.map(renderField)}

      {shouldShowBillSummary && <BillSummary tableData={tableOption} />}

      <FormSubmitButton
        label="Update"
        loading={loading}
        colSize="col-12"
        align="end"
      />
    </PageBody>
  );
};

export default EditParkingBookingForm;